import React, { PropsWithChildren } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DropdownMenu as RadixDropdownMenu } from "radix-ui";
import { ChevronDown, SquareArrowOutUpRight } from "lucide-react";

type DropdownMenuProps<T> = PropsWithChildren<{
  options: T[];
  getOptionLabel: (opt: T) => string;
  getIconBefore?: (opt: T) => React.ReactNode | undefined;
  getIconAfter?: (opt: T) => boolean | undefined;
  getOptionUrl?: (opt: T) => string | undefined;
  onSelectOption?: (opt: T) => void;
  icon?: React.ReactNode;
  contentProps?: React.ComponentProps<typeof RadixDropdownMenu.Content>;
  rootProps?: React.ComponentProps<typeof RadixDropdownMenu.Root>;
  itemProps?: React.ComponentProps<typeof RadixDropdownMenu.Item>;
  triggerProps?: React.ComponentProps<typeof RadixDropdownMenu.Trigger>;
}>;

export function DropdownMenu<T>({
  children,
  options,
  getOptionLabel,
  getIconBefore,
  getIconAfter,
  getOptionUrl,
  onSelectOption,
  icon = <ChevronDown />,
  contentProps,
  rootProps,
  itemProps,
  triggerProps,
}: DropdownMenuProps<T>) {
  return (
    <RadixDropdownMenu.Root {...rootProps}>
      <RadixDropdownMenu.Trigger asChild>
        <button
          {...triggerProps}
          className={clsx(
            twMerge(
              "h-12 w-12 rounded-md p-2 hover:cursor-pointer hover:bg-blue-50",
              triggerProps?.className,
            ),
          )}
          aria-label="Dropdown menu"
        >
          {icon}
        </button>
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          {...contentProps}
          className={clsx(
            twMerge(
              "bg-surface-default z-popover relative rounded-md border border-blue-500 shadow-lg",
              contentProps?.className,
            ),
          )}
        >
          {children ??
            options.map((option: T, index: number) => {
              const iconBefore = getIconBefore?.(option);
              const iconAfter = getIconAfter?.(option);
              const optionUrl = getOptionUrl?.(option);

              return (
                <RadixDropdownMenu.Item
                  {...itemProps}
                  className={clsx(
                    twMerge(
                      "flex items-center justify-between px-4 py-3 first-of-type:rounded-t-md last-of-type:rounded-b-md hover:cursor-pointer hover:!border-none hover:bg-blue-50 hover:outline-0 focus:bg-blue-50 focus:outline-0",
                      itemProps?.className,
                    ),
                  )}
                  key={index}
                  onSelect={() => onSelectOption?.(option)}
                >
                  <div className="flex items-center gap-2">
                    {iconBefore && (
                      <span className="[&>svg]:!text-iconDefault flex-shrink-0">
                        {iconBefore}
                      </span>
                    )}
                    {optionUrl ? (
                      <a
                        href={optionUrl}
                        {...(iconAfter && {
                          target: "_blank",
                          rel: "noopener noreferrer",
                        })}
                        className="text-textDefault block no-underline"
                      >
                        {getOptionLabel(option)}
                      </a>
                    ) : (
                      <span>{getOptionLabel(option)}</span>
                    )}
                  </div>
                  {iconAfter && (
                    <span className="ml-2 flex-shrink-0">
                      <SquareArrowOutUpRight
                        size={20}
                        className="text-gray-400"
                      />
                    </span>
                  )}
                </RadixDropdownMenu.Item>
              );
            })}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}
